import {
  Component,
  computed,
  effect,
  inject,
  OnDestroy,
  ChangeDetectionStrategy,
  WritableSignal,
  signal,
} from "@angular/core";
import { ASSET_TYPES } from "../../../../enums/assetTypes.enum";
import { GateFaultTableComponent } from "../../../../shared/widgets/gate-fault-table/gate-fault-table.component";
import { ServiceListComponent } from "../../../../shared/widgets/service-list/service-list.component";
import * as L from "leaflet";

import { GatePositionComponent } from "../../../../shared/widgets/gate-position/gate-position.component";
import { ExpectedEnergyLossComponent } from "../../../../shared/widgets/expected-energy-loss/expected-energy-loss.component";
import { GateFaultFrequencyComponent } from "../../../../shared/widgets/gate-fault-frequency/gate-fault-frequency.component";
import { TranslateModule, TranslateService } from "@ngx-translate/core";
import { environment } from "../../../../../environments/environment";

import { ActivatedRoute } from "@angular/router";
import { createTimeoutSignal } from "../../../../shared/utils/timeout.utils";
import { TimeoutComponent } from "../../../../shared/components/timeout/timeout.component";
import { ProgressSpinnerModule } from "@openng/optimus-ui/progressspinner";
import { Position } from "../../../../models/shared/position";
import { NgClass } from "@angular/common";
import { SimplebarAngularModule } from "simplebar-angular";
import { GateSystem } from "../../../../models/api/asset/gateSystem";
import { GateStatusService } from "./facade/gate-status.service";
import { SupportedLang } from "../../../../models/ui/supportedLangs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { SEVERITY } from "../../../../constants/severities";
import { GateStatusStateService } from "../../../../services/state/gate-status/gate-status-state.service";

@Component({
  standalone: true,
  selector: "app-gate-status-detail",
  imports: [
    GateFaultTableComponent,
    ServiceListComponent,
    GatePositionComponent,
    ExpectedEnergyLossComponent,
    GateFaultFrequencyComponent,
    TranslateModule,
    TimeoutComponent,
    ProgressSpinnerModule,
    NgClass,
    SimplebarAngularModule,
    SimplebarAngularModule,
  ],
  templateUrl: "./gate-status-detail.component.html",
  styleUrl: "./gate-status-detail.component.scss",
  providers: [GateStatusService, GateStatusStateService],
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class GateStatusDetailComponent implements OnDestroy {
  public assetTypes = ASSET_TYPES;
  public map!: L.Map;
  private tileLayer!: L.TileLayer;

  public statusFacade = inject(GateStatusService);
  public translate = inject(TranslateService);

  private marker!: L.Marker | null;
  private route = inject(ActivatedRoute);

  public loadingMap = computed(
    () => this.statusFacade.gateSystemDetail() === null && this.statusFacade.gateSystemCoordinates() === null
  );
  public timeOutMap = createTimeoutSignal(this.loadingMap);
  protected gateSystemId: WritableSignal<string | null> = signal(null);

  constructor() {
    effect(() => {
      const gateSystem = this.statusFacade.gateSystemDetail();
      const gateSystemCoordinates = this.statusFacade.gateSystemCoordinates();
      if (gateSystem && gateSystemCoordinates) {
        if (this.map) {
          this.map.off(); // Remove all event listeners
          this.map.remove(); // Properly destroy the map
          this.map = null as any; // Clear the reference to the map instance
        }

        this.initMap(gateSystemCoordinates, gateSystem);
        this.map.invalidateSize();
      }
    });
    this.translate.onLangChange.pipe(takeUntilDestroyed()).subscribe({
      next: (langChange) => {
        const newLang = langChange.lang as SupportedLang;

        this.changeTiles(newLang);
      },
    });
    this.route.params.pipe(takeUntilDestroyed()).subscribe({
      next: (params) => {
        this.gateSystemId.set(params["id"]);
        this.statusFacade.loadInitialState(params["id"]);
      },
    });
  }

  public ngOnDestroy() {
    this.map?.remove();
    this.statusFacade.destroy();
  }
  /**
   * Initializes a marker on the map with the given latitude and longitude.
   * @param position
   * @param status
   * @param assetName
   * @returns a new marker if the position is valid, null otherwise
   */
  private initMarker(position: Position, status: string, assetName: string) {
    const lat = position.latitude;
    const lng = position.longitude;
    if (lat && lng) {
      return L.marker([lat, lng], {
        icon: this.getIcon(status, assetName),
      });
    } else {
      return null;
    }
  }
  /**
   * Gets an icon URL for a marker based on the given status and asset name.
   * @param status The status of the asset
   * @param assetName The name of the asset
   * @returns A Leaflet icon object with a divIcon containing the icon and the asset name
   */
  private getIcon(status: string, assetName: string) {
    let iconUrl: string;
    switch (status) {
      case SEVERITY.OK.status:
        iconUrl = SEVERITY.OK.iconUrl;
        break;
      case SEVERITY.MINOR.status:
        iconUrl = SEVERITY.MINOR.iconUrl;
        break;
      case SEVERITY.CRITICAL.status:
        iconUrl = SEVERITY.CRITICAL.iconUrl;
        break;
      case SEVERITY.MAJOR.status:
        iconUrl = SEVERITY.MAJOR.iconUrl;
        break;
      default:
        iconUrl = SEVERITY.UNKNOWN.iconUrl;
        break;
    }
    return L.divIcon({
      className: "mapDivIcon",
      html: `
        <div class="markerContainer">

          <img src="${iconUrl}" style="width: 25px!important; height: 25px!important;"/> 
          <span class="markerLabel" >${assetName}</span>

      </div>
      `,
      iconSize: [10, 10], // size of the icon
    });
  }

  /**
   * Initializes the map with the specified coordinates and gate system details.
   * If the map already exists, it will be removed and recreated at the new location.
   * A marker will be added to the map using the gate system's coordinates, severity, and label.
   *
   * @param coordinates The geographical coordinates (latitude and longitude) for the map's center.
   * @param gateSystem The gate system details used to initialize the marker.
   */
  private initMap(coordinates: Position, gateSystem: GateSystem) {
    if (coordinates && coordinates.latitude && coordinates.longitude) {
      this.map?.remove();
      this.map = L.map("statusMap", environment.mapConfig).setView([coordinates.latitude, coordinates.longitude], 12);
      const currentLang: SupportedLang = this.translate.currentLang as SupportedLang;

      const tilesUrl = environment.mapTileUrl[currentLang];
      if (tilesUrl) {
        this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);

        this.marker = this.initMarker(coordinates, gateSystem.severity ?? "", gateSystem.label);

        this.marker?.addTo(this.map);
        setTimeout(() => {
          this.map.invalidateSize();
        }, 500);
      }
    }
  }
  /**
   * Changes the map tiles to match the specified language.
   * Removes the current tile layer and adds a new one based on the new language.
   * Invalidates the map size to ensure proper rendering after changing tiles.
   *
   * @param newLang The new language for which to update the map tiles.
   */
  private changeTiles(newLang: SupportedLang) {
    if (this.map && this.tileLayer) {
      this.map.removeLayer(this.tileLayer);
      const tilesUrl = environment.mapTileUrl[newLang];
      this.tileLayer = L.tileLayer(tilesUrl).addTo(this.map);
      setTimeout(() => {
        this.map?.invalidateSize();
      }, 50);
    }
  }
}
